import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, CardTitle, CardDescription } from '../../components/ui/Card';
import { Package, ShoppingCart, Users, DollarSign, Calendar, Clock } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { CriticalBookingsAlert } from '../../components/CriticalBookingsAlert';
import { BookingDetailsModal } from '../../components/BookingDetailsModal';

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [criticalDaysThreshold, setCriticalDaysThreshold] = useState(3);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, timelineRes, todayRes, allBookingsRes, configRes] = await Promise.all([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/dashboard/timeline'),
        api.get('/admin/dashboard/today'),
        api.get('/admin/bookings'), // Get ALL bookings, not just upcoming/unshipped
        api.get('/admin/config'),
      ]);

      if (statsRes.success) {
        setStats(statsRes.stats);
      }

      if (timelineRes.success) {
        setTimeline(timelineRes.timeline);
      }

      if (todayRes.success) {
        setTodayEvents(todayRes.events);
      }

      if (allBookingsRes.bookings) {
        // Filter to only confirmed bookings with future events
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        console.log('[Dashboard] DEBUG - Total bookings:', allBookingsRes.bookings.length);
        console.log('[Dashboard] DEBUG - Today:', today);
        console.log('[Dashboard] DEBUG - First booking sample:', allBookingsRes.bookings[0]);

        const futureBookings = allBookingsRes.bookings.filter(booking => {
          // Backend returns camelCase (bookingId, startDate, etc.)
          const bookingId = booking.bookingId || booking.booking_id;
          const status = booking.status;

          console.log('[Dashboard] DEBUG - Checking booking:', bookingId, 'status:', status);

          if (status !== 'confirmed') {
            console.log('[Dashboard] DEBUG - Filtered out: not confirmed');
            return false;
          }

          const startDate = new Date(booking.startDate || booking.start_date || booking.eventDate || booking.event_date);
          console.log('[Dashboard] DEBUG - Start date:', startDate, 'vs today:', today, 'future?', startDate >= today);

          return startDate >= today;
        }).sort((a, b) => {
          const dateA = new Date(a.startDate || a.start_date || a.eventDate || a.event_date);
          const dateB = new Date(b.startDate || b.start_date || b.eventDate || b.event_date);
          return dateA - dateB;
        });

        console.log('[Dashboard] DEBUG - Future bookings filtered:', futureBookings.length);
        console.log('[Dashboard] DEBUG - Future bookings:', futureBookings);

        setUpcomingBookings(futureBookings);
      }

      if (configRes.success && configRes.config) {
        setCriticalDaysThreshold(configRes.config.criticalDaysThreshold);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDateTime = (timestamp) => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  };

  const getDaysUntilEvent = (booking) => {
    // Support both camelCase and snake_case field names
    const startDate = booking.startDate || booking.start_date || booking.eventDate || booking.event_date;
    const eventDate = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTimelineIcon = (type) => {
    switch (type) {
      case 'booking':
        return <Calendar className="w-5 h-5 text-green-600" />;
      case 'order':
        return <ShoppingCart className="w-5 h-5 text-blue-600" />;
      case 'customer':
        return <Users className="w-5 h-5 text-purple-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  // Critical section shows only unshipped bookings with ≤ criticalDaysThreshold days until event
  // "Anstehende Events" section shows ALL upcoming bookings chronologically

  console.log('[Dashboard] DEBUG - criticalDaysThreshold:', criticalDaysThreshold);
  console.log('[Dashboard] DEBUG - upcomingBookings count:', upcomingBookings.length);

  // Show ALL upcoming bookings (already sorted chronologically from API)
  const allUpcomingEvents = upcomingBookings;

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-400 mt-4">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Fotoboxen',
      value: stats.fotoboxen.total.toString(),
      change: `${stats.fotoboxen.variants} Varianten`,
      icon: Package,
      color: 'text-blue-600',
    },
    {
      name: 'Buchungen',
      value: stats.bookings.total.toString(),
      change: `${stats.bookings.upcoming} anstehend`,
      icon: ShoppingCart,
      color: 'text-green-600',
    },
    {
      name: 'Kunden',
      value: stats.customers.total.toString(),
      change: `+${stats.customers.new} neu`,
      icon: Users,
      color: 'text-purple-600',
    },
    {
      name: 'Umsatz',
      value: formatCurrency(stats.revenue.total),
      change: `${formatCurrency(stats.revenue.thisMonth)} diesen Monat`,
      icon: DollarSign,
      color: 'text-orange-600',
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-400 mt-1">Willkommen zurück! Hier ist eine Übersicht deines Geschäfts.</p>
      </div>

      {/* Critical Bookings Alert */}
      <CriticalBookingsAlert onRefresh={loadDashboardData} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.name} hover>
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-sm text-success mt-1">{stat.change}</p>
                </div>
                <div className={`${stat.color} bg-opacity-10 p-3 rounded-lg`}>
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Activity and Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Letzte Aktivitäten</CardTitle>
            <CardDescription>Die neuesten Ereignisse in deinem System</CardDescription>
          </CardHeader>
          <CardBody>
            {timeline.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Keine aktuellen Aktivitäten</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {timeline.slice(0, 10).map((item, index) => (
                  <div key={index} className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <div className="mt-0.5">{getTimelineIcon(item.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-sm text-gray-500 truncate">{item.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDateTime(item.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader>
            <CardTitle>Anstehende Events</CardTitle>
            <CardDescription>Alle kommenden Fotobox-Buchungen (chronologisch)</CardDescription>
          </CardHeader>
          <CardBody>
            {allUpcomingEvents.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Keine anstehenden Events</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allUpcomingEvents.map((booking) => {
                  const daysUntil = getDaysUntilEvent(booking);
                  const startDate = booking.startDate || booking.start_date || booking.eventDate || booking.event_date;
                  const productTitle = booking.productTitle || booking.product_title;
                  const variantTitle = booking.variantTitle || booking.variant_title;
                  const customerName = booking.customerName || booking.customer_name;
                  const customerEmail = booking.customerEmail || booking.customer_email;
                  const shippingStatus = booking.shippingStatus || booking.shipping_status || 'not_shipped';
                  const bookingId = booking.bookingId || booking.booking_id;

                  // Determine if booking is critical (within threshold and not shipped)
                  const isCritical = daysUntil <= criticalDaysThreshold && ['not_shipped', 'preparing'].includes(shippingStatus);

                  // Status badge styling
                  const getStatusBadge = () => {
                    switch (shippingStatus) {
                      case 'shipped':
                        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">✓ Versendet</span>;
                      case 'preparing':
                        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">⏳ In Vorbereitung</span>;
                      case 'delivered':
                        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">📦 Zugestellt</span>;
                      default:
                        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">⚠️ Noch nicht versendet</span>;
                    }
                  };

                  return (
                    <div
                      key={bookingId}
                      className={`p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer ${
                        isCritical
                          ? 'bg-red-50 border-red-300 hover:bg-red-100'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedBooking(booking)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-medium text-gray-900">
                              {productTitle}
                              {variantTitle && ` - ${variantTitle}`}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">
                            {customerName || customerEmail}
                          </p>
                          <div className="mt-2">
                            {getStatusBadge()}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-sm font-medium ${isCritical ? 'text-red-700' : 'text-blue-600'}`}>
                            {new Date(startDate).toLocaleDateString('de-DE')}
                          </p>
                          <p className={`text-xs mt-1 font-semibold ${isCritical ? 'text-red-600' : 'text-gray-500'}`}>
                            in {daysUntil} {daysUntil === 1 ? 'Tag' : 'Tagen'}
                          </p>
                          {isCritical && (
                            <p className="text-xs text-red-600 font-bold mt-1">KRITISCH!</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onUpdate={loadDashboardData}
        />
      )}
    </div>
  );
}
